// Asset.jsx
import React, { useContext, useEffect, useState, useRef } from "react";
import StackIcon from "tech-stack-icons"; // per your instruction
import { MuiTextField } from "../../components/MuiComponents/FormInputs";
import EditIcon from "../../components/SvgIcons/EditIcon";
import { CancelOutlined, CheckCircleOutline, Close, CloseOutlined, EditOutlined, GppGoodOutlined, OpenInNew, PlayArrowOutlined, Share, ShareOutlined, TaskAlt, WatchLaterOutlined } from "@mui/icons-material";
import { getTechIcon, platformMapping, showPlatformIcon } from "../../actions/commonActions";
import { useDispatch, useSelector } from "react-redux";
import { closeExplore } from "../../redux/reducers/dashboard";
import Typography from "../../components/Typography";
import SeverityBreakdown from "../../components/Common/SeverityBreakdown";
import moment from "moment";
import { getRiskColor, getRiskColorHex, getRiskTitle } from "../../utils/constants/findings";
import AssetCriticalityDropDown from "../../components/Inputs/AssetCriticalityDropDown";
import { assetCriticalityMapping, reverseAssetCriticalityMapping } from "../../utils/constants/asset";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useAssetManagementById, useUpdateAssetById } from "../../hooks/asset";
import { AlertContext } from "../../components/SuccessOrFailedModal";
import { APP_MODE, assessmentTypeMap, AssetCriticality, capitalizeFirstLetter, getSeverityTitle } from "../../utils/constants";
import MainLoader from "../../components/Loader/MainLoader";
import DropdownButton from "../../components/Button/DropdownButton";
import { StartNewGenPT, StartNewPentest } from "../../components/Header/Menu";
import ScheduleGenPTModal, { ScheduleGenPTDropdownItem } from "../pages/GenPT/components/ScheduleGenPTModal";
import { IT_INFRASTRUCTURE, MOBILE_APP } from "../../utils/constants/assetTypeRegistry";
import GenericDropDown from "../../components/Inputs/GenericDropDown";
import { useUserRole } from "../../hooks/auth";
import { SIEMBA_ADMIN, SIEMBA_CUSTOMER, SIEMBA_TESTER } from "../../utils/roles";
import MuiTooltip from "../../components/MuiComponents/MuiTooltip";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LayoutContext } from '../../Providers/LayoutProvider';
import pluralize from "pluralize";



const Pill = ({ children, className = "" }) => (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm bg-white/80 border border-gray-200 shadow-sm ${className}`}>
        {children}
    </span>
);



export default function AssetV2() {
    const wrapperRef = useRef(null);
    const navigate = useNavigate();
    const { id: assetIdFromUrl } = useParams();
    const [searchParams] = useSearchParams();
    const role = useUserRole();
    const { mainUrl } = useContext(LayoutContext);

    const close = () => {
        if (assetIdFromUrl === undefined) {
          dispatch(closeExplore())
        } else {
          navigate(-1)
        }
    }

    const editAllowedUsers = [SIEMBA_ADMIN, SIEMBA_TESTER, SIEMBA_CUSTOMER].includes(role)

    
    const dispatch = useDispatch();
    const exploreState = useSelector(state => state.dashboard.explore);
    const assetId = exploreState?.id || assetIdFromUrl;
    const { data, isLoading, error } = useAssetManagementById(assetId);
    const asset = data?.asset;

    const targetUrl = asset?.url
    const testHistory = asset?.testHistory
    const lastSuccessfulTest = testHistory?.lastSuccessfulTest
    const inProgressTests = testHistory?.inProgressTests
    const completedTests = testHistory?.completedTests
    const hasValidTestHistory = testHistory?.hasValidTestHistory

    const riskScore = asset?.keyInsight?.riskScore || '1'
    const assetType = asset?.assetType || ''
    const initialCriticality = assetCriticalityMapping[asset?.assetCriticality] || null
    const initialExposure = asset?.assetExposure || ''
    const initialName = asset?.name || ''
    const pentestAssetId = exploreState?.info?.pentest_asset_id || searchParams.get('pentest_asset_id') || ''
    const pentestId = exploreState?.info?.pentest_id || searchParams.get('pentest_id') || ''

    const [editCriticality, setEditCriticality] = useState(false)
    const [selectedCriticality, setSelectedCriticality] = useState(initialCriticality);
    const [selectedExposure, setSelectedExposure] = useState(initialExposure);
    const [editName, setEditName] = useState(false);
    const [selectedName, setSelectedName] = useState(initialName);
    const [editExposure, setEditExposure] = useState(false);
    const isErrorName = selectedName.trim() === '';

    const [scheduleModal, setScheduleModal] = useState({
        open: false,
        targetURL: '',
        assetId: null,
        assetDisplayName: '',
        platformCategory: 'web_app',
        assetExposure: null,
    });

    const exposureOptions = [
        { value: 'internal', label: 'Internal' },
        { value: 'external', label: 'External' }
    ];


    const [popup] = useContext(AlertContext)
    const mutation = useUpdateAssetById(assetId);
    const criticalityOptions = Object.keys(AssetCriticality).map(key => ({
        value: key,
        label: AssetCriticality[key].title,
        tooltip: AssetCriticality[key].description
    }));

    useEffect(() => {
        function handleClickOutside(event) {
            // The START dropdown (DropdownButton) renders its menu via createPortal to
            // document.body, so clicks on "Run GenPT"/"Run Pentest"/"Schedule GenPT" land
            // outside wrapperRef. Ignore those so the preview does not close/navigate away
            // before the selected item's redirect can run (ROAR-4557).
            if (event.target?.closest?.('[role="menu"]')) {
                return;
            }
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                console.log('Edit states:', {
                    editName,
                    editCriticality,
                    editExposure
                });
                if (!editName && !editCriticality && !editExposure) {
                    close();
                }

            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editName, editCriticality, editExposure]);


    const handleChange = (selectedOption) => {
        setSelectedCriticality(assetCriticalityMapping[selectedOption?.value]);
    };

    async function handleCriticalityUpdate() {
        if (selectedCriticality && initialCriticality !== selectedCriticality) {
            popup({
                title: "Are you sure?",
                variant: "",
                description: "Changing the Asset Criticality will affect the risk score and security posture of this asset. This may trigger re-evaluation of the asset metrics. Are you sure you want to proceed?",
                okayText: "YES, MODIFY",
                cancelText: "GO BACK"
            }).then(async () => {
                await mutation.mutateAsync({
                    priority: reverseAssetCriticalityMapping[selectedCriticality],
                    pentestAssetId: pentestAssetId
                });
                setEditCriticality(false);
            });
        }
    }

    async function handleNameUpdate() {
        console.log("== editName", editName)
        if (selectedName && initialName !== selectedName && !isErrorName) {
            popup({
                title: "Are you sure?",
                variant: "",
                description: "Changing the asset details will update this asset throughout the platform. Are you sure you want to proceed?",
                okayText: "SAVE CHANGES",
                cancelText: "GO BACK"
            }).then(async () => {
                await mutation.mutateAsync({
                    name: selectedName,
                    pentestAssetId: pentestAssetId
                });
                setEditName(false);
            });

        }
    }

    async function handleExposureUpdate() {
        if (selectedExposure && initialExposure !== selectedExposure) {
            popup({
                title: "Are you sure?",
                variant: "",
                description: "Changing the asset details will update this asset throughout the platform. Are you sure you want to proceed?",
                okayText: "SAVE CHANGES",
                cancelText: "GO BACK"
            }).then(async () => {
                await mutation.mutateAsync({
                    mode: selectedExposure,
                    pentestAssetId: pentestAssetId
                });
                setEditExposure(false);
            });

        }
    }

    const handleTestClick = (test, assetId, mainUrl, navigate, close) => {
        if (!test) return;

        switch (test.tool) {
            case 'genpt':
                if (test.status == 'inprogress') {
                    navigate(`${mainUrl}/genpt/tests/chat/${test.chat_id}`);
                } else {
                    navigate(`${mainUrl}/genpt/tests/${test.id}`);
                }
                break;
            case 'genva':
                navigate(`${mainUrl}/all_va/${assetId}/dashboard/`);
                break;
            case 'ptaas':
                navigate(`${mainUrl}/all_pentests/${pentestId}/dashboard`);
                break;
            default:
                console.log(`Unhandled test tool`);
        }

        // close();
        dispatch(closeExplore());
    };



    useEffect(() => {
        if (initialCriticality && initialCriticality !== selectedCriticality) {
            setSelectedCriticality(initialCriticality)
        }

        if (initialExposure && initialExposure !== selectedExposure) {
            setSelectedExposure(initialExposure)
        }

        if (asset?.name && asset.name !== selectedName) {
            setSelectedName(asset.name)
        }
    }, [initialCriticality, initialExposure, asset?.name])

    // const isSubdomain = true



    return (
        <>
            <ScheduleGenPTModal
                open={scheduleModal.open}
                onClose={() =>
                    setScheduleModal({
                        open: false,
                        targetURL: '',
                        assetId: null,
                        assetDisplayName: '',
                        platformCategory: 'web_app',
                        assetExposure: null,
                    })
                }
                targetURL={scheduleModal.targetURL}
                assetId={scheduleModal.assetId}
                assetDisplayName={scheduleModal.assetDisplayName}
                platformCategory={scheduleModal.platformCategory}
                assetExposure={scheduleModal.assetExposure}
            />
            {isLoading && <MainLoader />}
            <div ref={wrapperRef} className="rounded-xl">
                {/* {isLoading && <MainLoader />} */}
                {/* Header */}
                <div className="mx-3 md:mx-5 flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1 text-left">
                        {editName ? (
                            <div className="flex flex-wrap items-center gap-2">
                                <MuiTextField
                                    value={selectedName}
                                    onChange={(e) => setSelectedName(e.target.value)}
                                    variant="standard"
                                    InputProps={{ disableUnderline: true }}
                                    inputProps={{ maxLength: 100 }}
                                    autoFocus
                                    sx={{
                                        backgroundColor: "var(--background)",
                                        color: 'black',
                                        borderRadius: "8px",
                                        width: "auto",
                                        // maxWidth: "100px",
                                        height: "36px",
                                        "& .MuiInput-root": {
                                            // backgroundColor: "var(--background)",
                                            // color: 'black',
                                            // height: "100%",
                                            // "&:before, &:after": {
                                            //     borderBottom: "none !important",
                                            // },
                                            // "&.Mui-focused": {
                                            //     backgroundColor: "var(--background)",
                                            // },
                                            // "&:hover, &:focus, &.Mui-active": {
                                            //     backgroundColor: "var(--background)",
                                            // },
                                            "& .MuiInputBase-input": {
                                                color: 'black',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                letterSpacing: '0.15px',
                                                padding: "0 10px",
                                                height: "36px",
                                                display: "flex",
                                                alignItems: "center",
                                            },
                                        },
                                    }}
                                />

                                <div className="flex gap-2 mt-2">
                                    <Close
                                        className="cursor-pointer"
                                        style={{ height: 20, display: 'flex' }}
                                        onClick={() => {
                                            setEditName(false);
                                            setSelectedName(asset?.name || '');
                                        }}
                                    />
                                    {!isErrorName && <SaveOutlinedIcon
                                        className="cursor-pointer"
                                        style={{ height: 20, display: 'flex' }}
                                        onClick={async () => handleNameUpdate()}
                                    />}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center gap-2">
                                <Typography
                                    variant="h6"
                                    className="text-xl font-semibold max-w-[300px] truncate"
                                    title={selectedName}
                                >
                                    {selectedName}
                                </Typography>
                                {editAllowedUsers && <EditOutlined
                                    className="cursor-pointer"
                                    style={{ height: 20 }}
                                    onClick={() => setEditName(true)}
                                />}
                            </div>
                        )}
                        <Typography variant="subtitle1" className="text-secondary-700 block mt-1">{asset?.url || ''}</Typography>
                    </div>
                    <div className="flex w-full sm:w-auto justify-end shrink-0 items-start gap-3 sm:pt-0.5">
                        {asset?.url && <div className="w-10 h-10 bg-secondary-50 rounded-md cursor-pointer flex items-center justify-center" title="Open" onClick={() => {
                            const url = asset?.url;
                            if (url) {
                                const fullUrl = url.startsWith('http') ? url : `https://${url}`;
                                window.open(fullUrl, '_blank');
                            }
                        }}>
                            <OpenInNew sx={{ color: 'var(--secondary-700)' }} />
                        </div>}
                        {/* <div className="w-10 h-10 bg-secondary-50 rounded-md flex items-center justify-center" title="Share">
                            <ShareOutlined sx={{ color: 'var(--secondary-700)' }} />
                        </div> */}
                        <div className="w-10 h-10 bg-secondary-50 rounded-md cursor-pointer flex items-center justify-center" title="Close" onClick={close}>
                            <Close sx={{ color: 'var(--secondary-700)' }} />
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-gray-200 shadow-sm mt-5" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}></div>

                {/* Key Insight */}
                <div className="mx-3 md:mx-5 mt-5 bg-primary-50 rounded-xl border p-4 shadow-sm" style={{ borderColor: 'var(--primary-500)' }}>
                    <div className="flex flex-col justify-between">
                        <div className="flex items-start gap-3">
                            <img src="/images/ai_line.svg" height={18} width={18} alt="" />
                            <div>
                                <Typography variant="subtitle2" className="text-primary-500 font-semibold">Key Insight</Typography>
                                <Typography variant="body2" className="mt-3">{asset?.keyInsight?.message || ''}</Typography>
                            </div>
                        </div>
                        <div className="h-px bg-primary-100 my-4" />
                        <div className="flex content-between justify-between items-center mx-8">
                            <div className="flex flex-col">
                                <Typography variant="subtitle2" className="font-semibold text-gray-500">Risk Score</Typography>
                                <div className="flex items-center gap-2 text-black">
                                    <Typography variant='subtitle1' className="text-black !font-semibold">
                                        {parseFloat(riskScore)?.toFixed(1)}
                                    </Typography>

                                    <div className="flex items-center gap-1 text-sm">
                                        <span
                                            className="inline-block h-2 w-2 rounded-sm"
                                            style={{ backgroundColor: getRiskColorHex(riskScore) }}
                                        />
                                        <Typography variant="body2" className="text-black">
                                            {getRiskTitle(riskScore)}
                                        </Typography>
                                    </div>
                                </div>


                            </div>

                            <div className="w-px h-12 bg-primary-100" />

                            <div className="flex flex-col">
                                <Typography variant="subtitle2" className="font-semibold text-gray-500">Total Open Findings</Typography>
                                <Typography variant="h6" className="font-semibold">{hasValidTestHistory && asset?.keyInsight?.totalOpenFindings ? asset?.keyInsight?.totalOpenFindings : '-'}</Typography>
                            </div>

                        </div>
                    </div>
                </div>


                {/* Asset Details */}
                <div className="mx-3 md:mx-5  mt-5">
                    <div className="flex items-center gap-x-2 mb-3">
                        <img src="/images/clarity_details.svg" alt="" />
                        <Typography variant="subtitle2" className="font-semibold text-gray-700">Asset Details</Typography>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
                        <div>
                            <Typography variant="caption" className="font-semibold text-gray-400">Asset Type</Typography>
                            <div className="mt-1 flex items-center gap-2">
                                <div className="inline-flex items-center gap-2 py-1">
                                    <Typography variant="subtitle1" className="font-bold">{platformMapping?.[assetType]}</Typography>
                                    <div className="bg-secondary-50 p-2 rounded flex items-center justify-center h-8 w-8">
                                        {showPlatformIcon(assetType)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Typography variant="caption" className="font-semibold text-gray-400">EASM Monitoring</Typography>
                            <div className="flex gap-x-2 items-center mt-1">
                                <Typography variant="subtitle1" className="font-bold">{asset?.monitoringEnabled ? 'Yes' : 'No'}</Typography>
                                {/* <Link className="cursor-pointer" to={isSubdomain ? `${mainUrl}/easm/attack_surface/subdomain/${assetId}` : `${mainUrl}/easm/monitoring/${assetId}/summary`}> */}
                                <MuiTooltip title={"EASM Dashboard"} placement="bottom">
                                    <OpenInNew sx={{ color: '#00000099', height: 16, width: 16 }} />
                                </MuiTooltip>
                                {/* </Link> */}
                            </div>
                        </div>

                        <div>
                            <Typography variant="caption" className="font-semibold text-gray-400">Asset Exposure</Typography>
                            {editExposure ? (
                                <div className="flex gap-x-2 items-center">
                                    <GenericDropDown
                                        options={exposureOptions}
                                        value={selectedExposure}
                                        onChange={(option) => setSelectedExposure(option.value)}
                                        showUpdateConfirmation={false}
                                        width={179}
                                        placeholder={"Set Property"}
                                        renderStyles={true}
                                        placeholderColor="var(--secondary-700)"
                                        menuBorderRadius={2}
                                        border={null}
                                        backgroundColor="#F6F9FC"
                                        borderColor="transparent"
                                    />
                                    <div
                                        className="cursor-pointer"
                                        onClick={() => {
                                            setSelectedExposure(initialExposure);
                                            setEditExposure(false);
                                        }}
                                    >
                                        <Close style={{ height: 15 }} />
                                    </div>

                                    <div className={"cursor-pointer"} onClick={async () => handleExposureUpdate()}>
                                        <SaveOutlinedIcon style={{ height: 15 }} />
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-1 font-medium flex items-center gap-2">
                                    {selectedExposure ? (
                                        <Typography variant="subtitle1" className="font-bold">
                                            {capitalizeFirstLetter(selectedExposure)}
                                        </Typography>
                                    ) : (
                                        <Typography variant="subtitle1" className="text-secondary-700">Set Property</Typography>
                                    )}
                                    {editAllowedUsers && <div className={"cursor-pointer"} onClick={() => setEditExposure(true)}>
                                        <EditOutlined style={{ height: 15 }} />
                                    </div>}
                                </div>
                            )}
                        </div>
                        <div>
                            <Typography variant="caption" className="font-semibold text-gray-400">Asset Criticality</Typography>
                            {editCriticality ? <div className="flex gap-x-2 items-center">
                                <GenericDropDown
                                    options={criticalityOptions}
                                    value={reverseAssetCriticalityMapping[selectedCriticality]}
                                    onChange={handleChange}
                                    showUpdateConfirmation={false}
                                    width={179}
                                    placeholder={""}
                                    renderStyles={true}
                                    placeholderColor="var(--functional-red-900)"
                                    menuBorderRadius={2}
                                    border={null}
                                    backgroundColor="#F6F9FC"
                                    borderColor="transparent"
                                />
                                <div className={"cursor-pointer"} onClick={() => {
                                    setSelectedCriticality(initialCriticality);
                                    setEditCriticality(false)
                                }}  >
                                    <Close style={{
                                        height: 15
                                    }} />
                                </div>

                                <div className={"cursor-pointer"} onClick={() => {
                                    handleCriticalityUpdate()
                                }}  >
                                    <SaveOutlinedIcon style={{
                                        height: 15
                                    }} />
                                </div>

                            </div> :
                                <div >
                                    {selectedCriticality ? <div className="mt-1 font-medium flex items-center gap-2">
                                        <Typography variant="subtitle1" className="font-bold">{selectedCriticality}</Typography>
                                        {editAllowedUsers && <div className={"cursor-pointer"} onClick={() => {
                                            setEditCriticality(true)
                                        }}  >
                                            <EditOutlined style={{
                                                height: 15
                                            }} />
                                        </div>}
                                    </div> : <Typography variant="subtitle1" className="font-bold">-</Typography>}
                                </div>
                            }
                        </div>
                    </div>

                    {/* Tech stack */}
                    <div className="mt-4">
                        <Typography variant="caption" className="font-semibold mb-2 text-gray-400">Technology Stack</Typography>
                        <div className="flex flex-wrap gap-2">
                            {asset && asset?.technologyStack?.length > 0 ? (
                                asset?.technologyStack?.map((tech, index) => (
                                    <Pill key={index}>
                                        <div className="w-4 h-4">
                                            {getTechIcon(tech?.tech_stack.toLowerCase())}
                                        </div>
                                        <Typography variant="subtitle1" className="font-bold"> {tech?.tech_stack}</Typography>
                                    </Pill>
                                ))
                            ) : (
                                <div className="flex flex-col gap-y-2">
                                    <div className="flex items-center justify-evenly w-full px-1 py-2 rounded-lg border border-gray-200 ">
                                        <GppGoodOutlined sx={{ color: "#3ADA7A" }} />
                                        <Typography variant="subtitle1" className="text-gray-600 font-medium">No Technology Stack Detected</Typography>
                                    </div>
                                    <Typography variant="body2" className="text-gray-500 text-center">Unable to detect; the asset appears protected.</Typography>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-gray-200 shadow-sm mt-5" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}></div>

                {/* Open Findings Breakdown */}
                <div className="mx-3 md:mx-5 mt-5">
                    <div className="flex items-center gap-x-2 mb-3">
                        <img src="/images/risk_analysis.svg" alt="" />
                        <Typography variant="subtitle2" className="font-semibold text-gray-700">Open Findings Breakdown and Analysis</Typography>
                    </div>

                    <div className="mt-4 grid grid-cols-5 gap-x-5 gap-y-5">
                        {(() => {
                            const breakdown = asset?.asset_finding_summary?.total_findings_breakdown;
                            const hasNonZeroValue = breakdown && Object.values(breakdown).some(value => value > 0);

                            const getValue = (severity) => {
                                if (!breakdown || !hasNonZeroValue) return '-';
                                return breakdown[severity] || '0';
                            };

                            return (
                                <>
                                    <div className="severity-critical severity-item !text-left !p-3 col-span-2 lg:col-span-1">
                                        <Typography variant="h6" className="font-bold text-red-700">{getValue('critical')}</Typography>
                                        <Typography variant="caption" className="text-red-600 mt-1 block">Critical</Typography>
                                    </div>
                                    <div className="severity-high severity-item !text-left  !p-3 col-span-2 lg:col-span-1">
                                        <Typography variant="h6" className="font-bold text-orange-600">{getValue('high')}</Typography>
                                        <Typography variant="caption" className="text-orange-600 mt-1 block">High</Typography>
                                    </div>
                                    <div className="severity-medium severity-item !text-left !p-3 col-span-2 lg:col-span-1">
                                        <Typography variant="h6" className="font-bold text-yellow-600">{getValue('medium')}</Typography>
                                        <Typography variant="caption" className="text-yellow-600 mt-1 block">Medium</Typography>
                                    </div>
                                    <div className="severity-low severity-item !text-left  !p-3 col-span-2 lg:col-span-1">
                                        <Typography variant="h6" className="font-bold text-amber-600">{getValue('low')}</Typography>
                                        <Typography variant="caption" className="text-amber-600 mt-1 block">Low</Typography>
                                    </div>
                                    <div className="severity-info severity-item !text-left  !p-3 col-span-2 lg:col-span-1">
                                        <Typography variant="h6" className="font-bold text-slate-600">{getValue('info')}</Typography>
                                        <Typography variant="caption" className="text-slate-500 mt-1 block">Info</Typography>
                                    </div>
                                </>
                            );
                        })()}
                    </div>

                    {/* OWASP Top 5 */}
                    {asset?.owaspTop5?.length > 0 && (
                        <div className="mt-4">
                            <Typography variant="subtitle2" className="font-semibold text-gray-700 mb-2">OWASP Top 5 Open Findings Breakdown</Typography>
                            <div className="space-y-2">
                                {asset?.owaspTop5?.map((item, index) => (
                                    <div key={index} className="flex items-center justify-between bg-white rounded-lg border p-3 shadow-sm">
                                        <div>
                                            <Typography variant="subtitle2" className="font-semibold">{item.name}</Typography>
                                            <Typography variant="caption" className="text-gray-500 block">{item.description}</Typography>
                                        </div>
                                        <div className="font-bold text-xl rounded-full w-[50px] h-[30px] flex items-center justify-center"
                                            style={{ border: '1px solid #F65C5C', backgroundColor: 'rgba(246, 92, 92, 0.1)', color: '#F65C5C' }}>
                                            {item.count.toString().padStart(2, '0')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-gray-200 shadow-sm mt-5" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}></div>

                {/* Test History & Analysis */}
                <div className="mx-3 md:mx-5 mt-5">
                    {hasValidTestHistory === false &&
                        <>
                            <div className="flex items-center gap-x-2 mb-3">
                                <img src="/images/outline_analysis.svg" alt="" />
                                <Typography variant="subtitle2" className="font-semibold text-gray-700">Test History & Analysis</Typography>
                            </div>
                            <div className="mt-5 bg-primary-50 rounded-xl border p-4 shadow-sm" style={{ borderColor: 'var(--primary-500)' }}>
                                <div className="flex flex-col justify-between">
                                    <div className="flex flex-col items-start gap-3">
                                        <Typography variant="subtitle1" className="text-primary-500 font-semibold">No Tests Run Yet!</Typography>
                                        <Typography variant="body2" className="font-semibold">Start your first assessment to identify vulnerabilities and secure this asset.</Typography>
                                        <DropdownButton
                                            reverseDropdown={true}
                                            icon={<PlayArrowOutlined />}
                                            title="START"
                                            className={'!min-w-[5.5rem] !px-4 !py-2 mt-2 !normal-case'}

                                            components={[
                                                <StartNewGenPT
                                                    key="genpt"
                                                    label="Run GenPT"
                                                    usePlayIcon
                                                    showIcon
                                                    query={`?url=${targetUrl}&type=${assetType}&id=${assetId}`}
                                                />,
                                                <StartNewPentest
                                                    key="pentest"
                                                    label="Run Pentest"
                                                    usePlayIcon
                                                    showIcon
                                                    disabled={APP_MODE === 'genptbeta'}
                                                    onStart={close}
                                                />,
                                                <ScheduleGenPTDropdownItem
                                                    key="schedule-genpt"
                                                    label="Schedule GenPT"
                                                    useCalendarIcon
                                                    showIcon
                                                    from="explore_asset"
                                                    isDisabled={assetType === MOBILE_APP || assetType === IT_INFRASTRUCTURE}
                                                    onScheduleClick={() =>
                                                        setScheduleModal({
                                                            open: true,
                                                            targetURL: targetUrl,
                                                            assetId,
                                                            assetDisplayName: selectedName || asset?.name || '',
                                                            platformCategory: assetType || 'web_app',
                                                            assetExposure: initialExposure,
                                                        })
                                                    }
                                                />,
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    }
                    {lastSuccessfulTest && Object.values(lastSuccessfulTest).length > 0 && <>
                        <div className="flex items-center gap-x-2 mb-3">
                            <img src="/images/outline_analysis.svg" alt="" />
                            <Typography variant="subtitle2" className="font-semibold text-gray-700">Test History & Analysis</Typography>
                        </div>
                        <div className="mt-5 bg-[#E0F8F733] rounded-xl border p-4 shadow-sm" style={{ borderColor: 'var(--primary-500)' }}>
                            <div className="flex gap-x-1 items-end">
                                <Typography variant="body2" className="text-gray-700 flex">
                                    Last successful test was
                                </Typography>
                                <Typography variant="body2" className="font-bold text-functional-red">{moment(new Date()).diff(lastSuccessfulTest?.date, 'days')} {pluralize('day', moment(new Date()).diff(lastSuccessfulTest?.date, 'days'))} ago</Typography>
                                <Typography variant="body2" className="text-gray-700 flex">
                                    using
                                </Typography>
                                <Typography variant="body2" className="font-semibold"> {assessmentTypeMap?.[lastSuccessfulTest?.tool]}.</Typography>
                            </div>
                            <div className="mt-3 flex gap-2 items-end justify-between">
                                <div className="flex gap-2 flex-wrap">
                                    {(() => {
                                        const findings = lastSuccessfulTest?.findings;
                                        const hasNonZeroValue = findings && Object.values(findings).some(value => value > 0);

                                        const getValue = (severity) => {
                                            if (!findings || !hasNonZeroValue) return '-';
                                            return findings[severity] || '0';
                                        };

                                        return (
                                            <>
                                                <div className="severity-critical severity-item !text-left w-[80px]">
                                                    <Typography variant="h6" className="font-bold text-red-700">{getValue('critical')}</Typography>
                                                    <Typography variant="caption" className="text-red-600 mt-1 block">Critical</Typography>
                                                </div>
                                                <div className="severity-high severity-item !text-left  w-[80px]">
                                                    <Typography variant="h6" className="font-bold text-orange-600">{getValue('high')}</Typography>
                                                    <Typography variant="caption" className="text-orange-600 mt-1 block">High</Typography>
                                                </div>
                                                <div className="severity-medium severity-item !text-left w-[80px]">
                                                    <Typography variant="h6" className="font-bold text-yellow-600">{getValue('medium')}</Typography>
                                                    <Typography variant="caption" className="text-yellow-600 mt-1 block">Medium</Typography>
                                                </div>
                                                <div className="severity-low severity-item !text-left w-[80px]">
                                                    <Typography variant="h6" className="font-bold text-amber-600">{getValue('low')}</Typography>
                                                    <Typography variant="caption" className="text-amber-600 mt-1 block">Low</Typography>
                                                </div>
                                                <div className="severity-info severity-item !text-left  w-[80px]">
                                                    <Typography variant="h6" className="font-bold text-slate-600">{getValue('info')}</Typography>
                                                    <Typography variant="caption" className="text-slate-500 mt-1 block">Info</Typography>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                                <Typography variant="body2" className="font-semibold text-gray-500">{lastSuccessfulTest?.date}</Typography>
                            </div>
                        </div>
                    </>}

                    {/* Latest Tests */}
                    <div className="mt-4 space-y-3">
                        {inProgressTests?.length > 0 && (
                            <>
                                <Typography variant="subtitle2" className={"text-headings mt-2.5"}>In-Progress Tests</Typography>
                                {inProgressTests.map((test, index) => (
                                    <div key={index} className="bg-background rounded-lg border p-3">
                                        <div className="flex items-center justify-between cursor-pointer flex-wrap">
                                            <div>
                                                <div
                                                    className="flex font-semibold gap-x-2 items-center group cursor-pointer"
                                                    onClick={() => handleTestClick(test, assetId, mainUrl, navigate, close)}

                                                >
                                                    <WatchLaterOutlined style={{ fontSize: 16, color: '#228EBC' }} />

                                                    {/* Text */}
                                                    <MuiTooltip
                                                        placement={'bottom'}
                                                        title={test?.title}
                                                    >
                                                        <div>
                                                            <Typography className="transition-all text-[#228EBC] group-hover:text-[#228EBC] group-hover:underline max-w-[300px] truncate">
                                                                {['genva', 'ptaas'].includes(test?.tool) ? (test?.title || assessmentTypeMap[test?.tool]) : (
                                                                    <>
                                                                        {assessmentTypeMap[test?.tool]}
                                                                        {test?.tool === 'genpt' && test?.session_id &&
                                                                            `- ${test.session_id.split('-')[1]}`
                                                                        }
                                                                    </>
                                                                )}

                                                            </Typography>
                                                        </div>
                                                    </MuiTooltip>


                                                    {/* Icon */}
                                                    {/* <OpenInNew
                                                        sx={{
                                                            height: 16,
                                                            width: 16,
                                                            color: '#00000099',
                                                            transition: 'color 0.2s ease',
                                                        }}
                                                        className="group-hover:text-[#228EBC]"
                                                    /> */}
                                                </div>

                                                <Typography variant="body2" className="mt-1 text-orange-600">Initiated: {moment(test?.date).format('MM/DD/YYYY')}</Typography>
                                                {test?.tool == 'ptaas' &&
                                                    <div
                                                        onClick={() => {
                                                            navigate(`${mainUrl}/all_assets/${pentestAssetId}/dashboard`)
                                                            // close()
                                                            dispatch(closeExplore())
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            className="mt-2 group flex items-center gap-1 cursor-pointer text-black hover:text-[#228EBC] hover:underline"
                                                        >
                                                            <OpenInNew
                                                                sx={{
                                                                    height: 14,
                                                                    width: 14,
                                                                    color: 'currentColor',
                                                                    transition: 'color 0.2s ease',
                                                                }}
                                                            />
                                                            View Asset Pentest Details
                                                        </Typography></div>
                                                }

                                            </div>
                                            <SeverityBreakdown breakdown={test?.findings} />
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}

                        {completedTests?.length > 0 && (
                            <>
                                <Typography variant="subtitle2" className={"text-headings mt-5"}>Completed Tests</Typography>
                                {completedTests.map((test, index) => {
                                    const hasAtLeastTwoGenva = completedTests.filter(t => t.tool === 'genva').length >= 2;
                                    const isFirstGenva = hasAtLeastTwoGenva && index === 0;
                                    const shouldHandleClick = !hasAtLeastTwoGenva || isFirstGenva;

                                    return (
                                        <div key={index} className="bg-background rounded-lg border p-3">
                                            <div className="flex items-center justify-between cursor-pointer flex-wrap">
                                                <div className="mb-2 sm:mb-0">
                                                    <div
                                                        className={`flex font-semibold gap-x-2 items-center group ${shouldHandleClick ? 'cursor-pointer' : ''}`}
                                                        onClick={shouldHandleClick ? () => handleTestClick(test, assetId, mainUrl, navigate, close) : undefined}

                                                    >
                                                        {test?.status === 'completed' ? <TaskAlt style={{ fontSize: 16, color: '#00C950' }} /> : <CloseOutlined style={{ fontSize: 16, color: '#FB2C36' }} />}

                                                        {/* Text */}
                                                        <MuiTooltip
                                                            placement={'bottom'}
                                                            title={test?.title}
                                                        >
                                                            <div>
                                                                <Typography className="transition-all text-[#228EBC] group-hover:text-[#228EBC] group-hover:underline max-w-[300px] truncate">
                                                                    {['genva', 'ptaas'].includes(test?.tool) ? (test?.title || assessmentTypeMap[test?.tool]) : (
                                                                        <>
                                                                            {assessmentTypeMap[test?.tool]}
                                                                            {test?.tool === 'genpt' && test?.session_id &&
                                                                                `- ${test.session_id.split('-')[1]}`
                                                                            }
                                                                        </>
                                                                    )}
                                                                </Typography>
                                                            </div>

                                                        </MuiTooltip>

                                                        {/* Icon */}
                                                        {/* <OpenInNew
                                                        sx={{
                                                            height: 16,
                                                            width: 16,
                                                            color: '#00000099',
                                                            transition: 'color 0.2s ease',
                                                        }}
                                                        className="group-hover:text-[#228EBC]"
                                                    /> */}
                                                    </div>

                                                    <Typography variant="body2" className="mt-1 text-orange-600">Completed: {moment(test?.date).format('MM/DD/YYYY')}</Typography>
                                                    {test?.tool == 'ptaas' &&
                                                        <div
                                                            onClick={() => {
                                                                navigate(`${mainUrl}/all_assets/${pentestAssetId}/dashboard`)
                                                                // close()
                                                                dispatch(closeExplore())
                                                            }}
                                                        >

                                                            <Typography
                                                                variant="body2"
                                                                className="mt-2 group flex items-center gap-1 cursor-pointer text-black hover:text-[#228EBC] hover:underline"
                                                            >
                                                                <OpenInNew
                                                                    sx={{
                                                                        height: 14,
                                                                        width: 14,
                                                                        color: 'currentColor',
                                                                        transition: 'color 0.2s ease',
                                                                    }}
                                                                />
                                                                View Asset Pentest Details
                                                            </Typography></div>
                                                    }

                                                </div>
                                                <SeverityBreakdown breakdown={test?.findings} />
                                            </div>
                                        </div>
                                    )
                                })}
                            </>
                        )}
                    </div>
                </div>

            </div>
        </>

    );
}
